import { useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';
import Actionbar from 'src/components/common/actionbar/Authactionbar';
import { Message } from 'src/components/common/form/Message';
import useUiuxStore from 'src/store/useUiuxStore';
import { useMouseContent } from 'src/utils/mouse/mouseContent';
import { BulkSection_HRM2120_R01 } from './BulkSection_HRM2120_R01';
import { Calendar_HRM2120_R01 } from './Calendar_HRM2120_R01';
import { HRM2120_R01Content } from './Content_HRM2120_R01';
import { DetailTable_HRM2120_R01 } from './DetailTable_HRM2120_R01';
import { HRM2120_R01DetailDto, HRM2120_R01HeaderGridRow } from './Dto_HRM2120_R01';
import { HRM2120_R01SearchSection } from './SearchSection_HRM2120_R01';
import { Table_HRM2120_R01 } from './Table_HRM2120_R01';

/** 사원 목록 왼쪽 너비와 이력 영역 위쪽 높이의 저장 설정이다. */
const WIDTH_RESIZER = {
    code: 'HRM2120_R01',
    type: 'width',
    storageKey: 'HRM2120_R01-split-width',
    min: 25,
    max: 65,
    default: 40,
} as const;
const HEIGHT_RESIZER = {
    code: 'HRM2120_R01',
    type: 'height',
    storageKey: 'HRM2120_R01-split-height',
    min: 20,
    max: 80,
    default: 45,
} as const;

/** 근무조 배정 화면을 검색, 일괄 배정, 이력과 저장된 배정 달력으로 조립한다. */
export const Container_HRM2120_R01 = () => {
    /** 조회용 사원 그리드와 편집용 배정 이력 그리드이다. */
    const gridRef = useRef<AgGridReact<HRM2120_R01HeaderGridRow>>(null);
    const detailGridRef = useRef<AgGridReact<HRM2120_R01DetailDto>>(null);
    /** 좌우 및 위아래 영역의 크기를 조절할 때 사용하는 참조와 상태이다. */
    const widthContainerRef = useRef<HTMLDivElement>(null);
    const widthInfoRef = useRef<HTMLDivElement>(null);
    const widthResizing = useRef(false);
    const heightContainerRef = useRef<HTMLDivElement>(null);
    const heightInfoRef = useRef<HTMLDivElement>(null);
    const heightResizing = useRef(false);
    const { splitValue: width, handleMouseDown: startWidth } = useMouseContent({
        resizer: WIDTH_RESIZER,
        isResizing: widthResizing,
        containerRef: widthContainerRef,
        infoRef: widthInfoRef,
    });
    const { splitValue: height, handleMouseDown: startHeight } = useMouseContent({
        resizer: HEIGHT_RESIZER,
        isResizing: heightResizing,
        containerRef: heightContainerRef,
        infoRef: heightInfoRef,
    });
    const { setMessage } = useUiuxStore();
    const {
        fetchData,
        handleHeaderRowClick,
        handleInsertClick,
        handleDeleteClick,
        handleSaveClick,
        handleBulkApply,
        handleExcelClick,
        initContainerData,
    } = HRM2120_R01Content({ gridRef, detailGridRef, setMessage });

    return (
        <div className="content-container">
            <Actionbar
                onFetchClick={fetchData}
                onCreateClick={handleInsertClick}
                onDeleteClick={handleDeleteClick}
                onSaveClick={handleSaveClick}
                onExcelClick={handleExcelClick}
                onInitClick={initContainerData}
            />
            <HRM2120_R01SearchSection />
            <BulkSection_HRM2120_R01 onApply={handleBulkApply} />
            <div className="flex flex-1 min-h-0 p-[12px] gap-[4px]" ref={widthContainerRef}>
                <div className="min-w-0 min-h-0" style={{ width: `${width}%` }}>
                    <Table_HRM2120_R01 gridRef={gridRef} onSelect={handleHeaderRowClick} />
                </div>
                <div
                    className="w-[6px] bg-gray-100 cursor-col-resize hover:bg-gray-300"
                    onMouseDown={event => startWidth('LR', event.nativeEvent)}
                />
                <div
                    className="flex flex-col min-w-0 min-h-0 gap-[4px]"
                    style={{ width: `${100 - width}%` }}
                    ref={widthInfoRef}
                >
                    <div className="flex flex-col flex-1 min-h-0 gap-[4px]" ref={heightContainerRef}>
                        <div className="min-h-0" style={{ height: `${height}%` }}>
                            <DetailTable_HRM2120_R01 gridRef={detailGridRef} handleInsertClick={handleInsertClick} />
                        </div>
                        <div
                            className="h-[6px] bg-gray-100 cursor-row-resize hover:bg-gray-300"
                            onMouseDown={event => startHeight('UD', event.nativeEvent)}
                        />
                        <div className="min-h-0" style={{ height: `${100 - height}%` }} ref={heightInfoRef}>
                            <Calendar_HRM2120_R01 />
                        </div>
                    </div>
                </div>
            </div>
            <Message />
        </div>
    );
};
